import {Injectable} from '@angular/core';
import {TokenService} from "../api/session/token.service";
import {Token} from "../api/session/token";
import {Persona} from "../model/personas/persona.model";
import {ApiService} from "../api/api.service";
import {InterfaceProviderService} from "../basic-entity-back/services/interface-provider.service";
import {JsonLdInterface} from "../api/entity-interface";
import {Usuario} from "../model/personas/usuario.model";
import {BehaviorSubject, Observable, of, Subject} from "rxjs";
import {map, switchMap, tap} from "rxjs/operators";
import {Router} from "@angular/router";
import {esClienteDelComercial, esComercialPuro} from './comercial';

@Injectable({
    providedIn: 'root'
})
export class SessionService {
    private _sessionProfile: Persona | Usuario | null = null;
    private _profileChanges = new Subject<void>();
    private _requestProfileUpdate = new Subject<void>();
    private _sessionProfileBS = new BehaviorSubject(this._sessionProfile);
    public profileChanges = this._profileChanges.asObservable();

    public get token(): Token {
        return this._tokenService.token;
    }

    public get profile(): Persona | Usuario | null {

        return this._sessionProfile;
    }

    public get profileAsObservable(): Observable<Persona | Usuario | null> {
        if (this.profile) {
            this._sessionProfileBS.next(this.profile);
        }
        return this._sessionProfileBS.asObservable();
    }

    public get mercureToken(): string {
        return this._tokenService.mercureToken;
    }

    public get user(): Usuario | null {
        if (this._sessionProfile == null) {
            return null;
        } else if (this._sessionProfile instanceof Persona) {
            return this._sessionProfile.usuario;
        } else {
            return this._sessionProfile;
        }
    }

    constructor(private _tokenService: TokenService,
                private _interfaceProvider: InterfaceProviderService,
                private _router: Router,
                private _api: ApiService) {
        this._tokenService.sessionChanges.subscribe(() => {
            this._redirectIfTokenExpired();
            this._requestProfileUpdate.next();
        });
        this._initProfileUpdateTracking();
        this._requestProfileUpdate.next();
    }

    private _initProfileUpdateTracking() {
        this._requestProfileUpdate.pipe(
            switchMap(() => {
                if (this.isLogged()) {
                    return this._api.get<JsonLdInterface>('me')
                        .pipe(ApiService.sTakeBody(),
                            map(response => {
                                const interf = this._interfaceProvider.interfaceForObject(response);
                                return interf ? interf.fromGetToModel(response) : null;
                            }));
                } else {
                    return of(null);
                }
            })
        ).subscribe(profile => {
            this._sessionProfile = profile;
            this._profileChanges.next();
        });
    }

    public hasRole(role: string): boolean {
        const roles = role.split(',');
        if (roles.length === 0) {
            return false;
        }
        return roles.filter(r => this._tokenService.token.roles.includes(r.trim())).length > 0;
    }

    /** ROLE_COMERCIAL sin ROLE_EMPLEADO. Usar esto, no hasRole('ROLE_COMERCIAL'). */
    public esComercial(): boolean {
        return esComercialPuro(this._tokenService.token.roles);
    }

    /** Si el cliente es del comercial que tiene la sesión abierta. */
    public esMiCliente(cliente: any): boolean {
        return esClienteDelComercial(cliente, this.profile);
    }

    public get sessionChanges() {
        return this._tokenService.sessionChanges;
    }

    public login(user: string, password: string): Promise<Token> {
        return this._tokenService.login({username: user, password: password});
    }

    public logout(): void {
        this._tokenService.logout();
    }

    public isLogged(): boolean {
        return this._tokenService.checkLogin();
    }

    public changeProfile(profile: Persona | Usuario): Promise<any> {
        return this._interfaceProvider
            .managerForResource(profile)
            .update(profile)
            .pipe(tap(() => this._requestProfileUpdate.next()))
            .toPromise();
    }

    private _redirectIfTokenExpired() {
        if (this.token.isExpired()) {
            setTimeout(() => this._router.navigateByUrl('/login', /* Removed unsupported properties by Angular migration: queryParams. */ {})
                .catch(err => console.error(err)));
        }
    }
}
